import React, { useState, useEffect } from 'react';
import { useNavigate, useParams,} from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from './firebase.config';
import { auth, googleProvider, signInWithPopup } from './firebase.config';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import ReCAPTCHA from "react-google-recaptcha";
import { useAuth } from '../auth/AuthContext';

const lightenHex = (hex, percent) => {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);

  const r = Math.min(255, (num >> 16) + amt);
  const g = Math.min(255, ((num >> 8) & 0x00ff) + amt);
  const b = Math.min(255, (num & 0x0000ff) + amt);

  return `#${(0x1000000 + r * 0x10000 + g * 0x100 + b)
    .toString(16)
    .slice(1)}`;
};

const TenantHome = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, loading, isSuperAdmin, isVerifier } = useAuth();

  useEffect(() => {
  if (loading) return;

  // SuperAdmin should stay on tenant login page
  if (isSuperAdmin) return;

  if (isLoggedIn) {
    navigate(`/t/${tenantId}/dashboard`, { replace: true });
  }
}, [loading, isLoggedIn, isSuperAdmin, tenantId, navigate]);


  const [branding, setBranding] = useState(null);
  const [loadingBrand, setLoadingBrand] = useState(true);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    // Store tenantId so logout can redirect back here later.
    sessionStorage.setItem('activeTenantLoginId', tenantId);

    const fetchBranding = async () => {
  try {
const docRef = doc(firestore, "TenantLandingPages", tenantId);
const snap = await getDoc(docRef);
if (snap.exists()) {
  setBranding(snap.data());
} else {
  setBranding(null);
}
  } catch (err) {
    setBranding(null);
  } finally {
    setLoadingBrand(false);
  }
};
    fetchBranding();
  }, [tenantId]);


  const handleSignIn = async () => {

    if (!captchaVerified) {
  alert("Please verify CAPTCHA first.");
  return;
}
    
  setIsSigningIn(true);

  try {
    await signInWithPopup(auth, googleProvider);

  } catch (err) {
    if (err?.code !== "auth/popup-closed-by-user") {
      alert("Sign-in failed.");
    }
  } finally {
    setIsSigningIn(false);
  }
};

const onCaptchaChange = (value) => {
  setCaptchaVerified(!!value);
};

const slides = [
  {
    title: "Audit App",
    subtitle: "COMPLIANCE & AUDIT PLATFORM",
    description:
      "A unified platform for managing CBAM compliance, energy audits, and sustainability assessments in one place.",
    image:
      "https://images.unsplash.com/photo-1517048676732-d65bc937f952",
  },
  {
    title: "Carbon Border Compliance",
    subtitle: "CBAM",
    description:
      "Monitor carbon emissions, assess CBAM requirements, and manage emission data to support accurate regulatory compliance.",
    image:
      "https://images.unsplash.com/photo-1497436072909-60f360e1d4b1",
  },
  {
    title: "Energy Audit & Assessment",
    subtitle: "ENERGY AUDIT",
    description:
      "Capture energy consumption data, evaluate energy performance, and identify opportunities for improved efficiency and sustainability.",
    image:
      "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e",
  },
  {
    title: "One Platform. Complete Audit.",
    subtitle: "CBAM + ENERGY AUDIT",
    description:
      "Bring carbon compliance and energy auditing together with a single platform for data collection, analysis, reporting, and audit management.",
    image:
      "https://images.unsplash.com/photo-1556761175-b413da4baf72",
  },
];

useEffect(() => {
  const timer = setInterval(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, 5000);

  return () => clearInterval(timer);
}, [slides.length]);

  if (loadingBrand) {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}><CircularProgress /></Box>
  );
  }
  if (!branding) {
  return (
    <Box sx={{ p: 6, textAlign: "center" }}><Typography>Unknown organization.</Typography></Box>
  );
  }

  const brandColor = branding.brandColor || '#7C3AED';

  const gradient = `linear-gradient(
135deg,
${lightenHex(brandColor, 25)} 0%,
${brandColor} 100%
)`;

  return (
  <Box
  sx={{
    display: "flex",
    minHeight: "100vh",
    overflow: "hidden",
  }}
>
    <Box
  sx={{
    flex: 2,
    position: "relative",
    backgroundImage: `
linear-gradient(
rgba(10,20,40,.75),
rgba(10,20,40,.75)
),
url(${slides[currentSlide].image})
`,

backgroundSize: "cover",

backgroundPosition: "center",

transition: "background-image .8s ease",
    color: "#fff",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    overflow: "hidden",

    p: 8,
  }}
>

    <Box
  sx={{
    position: "absolute",
    top: 80,
    right: 120,
    width: 220,
    height: 220,
    borderRadius: "50%",
    bgcolor: "rgba(255,255,255,.08)",
    filter: "blur(5px)",
  }}
/>

<Box
  sx={{
    position: "absolute",
    bottom: 80,
    left: -70,
    width: 300,
    height: 300,
    borderRadius: "50%",
    bgcolor: "rgba(255,255,255,.05)",
  }}
/>

    <Box
  key={currentSlide}
  sx={{
    animation: "fadeIn .8s ease",
    maxWidth: 650,
  }}
>

        <Typography
            sx={{
                fontSize: 18,
                mb: 2,
                color: "#A5B4FC",
                letterSpacing: 2,
            }}
        >
            {slides[currentSlide].subtitle}
        </Typography>

        <Typography
            sx={{
                fontSize: 56,
                fontWeight: 700,
                lineHeight: 1.1,
            }}
        >
            {slides[currentSlide].title}
        </Typography>

        <Typography
            sx={{
                mt: 2,
                fontSize: 22,
                opacity: .9,
            }}
        >
            {slides[currentSlide].description}
        </Typography>
        <Box
  sx={{
    display: "flex",
    gap: 1,
    mt: 5,
  }}
>
  {slides.map((_, index) => (
    <Box
      key={index}
      sx={{
        width: currentSlide === index ? 36 : 10,
        height: 10,
        borderRadius: 20,
        bgcolor:
          currentSlide === index
            ? "#fff"
            : "rgba(255,255,255,.35)",
        transition: ".4s",
      }}
    />
  ))}
</Box>

    </Box>

</Box>

<Box
  sx={{
    width: "32%",
    minWidth: 430,
    bgcolor: "#fff",
    boxShadow: "0 25px 60px rgba(0,0,0,.12)",
    borderLeft: "1px solid #ECECEC",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    p: 6,
  }}
>
  <Box
    sx={{
      width: "100%",
      maxWidth: 340,
    }}
  >

    {/* LOGO + ORGANIZATION NAME */}
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        mb: 3,
      }}
    >

      {branding.logoUrl && (
        <Box
          component="img"
          src={branding.logoUrl}
          alt={branding.orgName}
          sx={{
            width: 82,
            height: 82,
            objectFit: "contain",
            mb: 1.5,
          }}
        />
      )}

      <Typography
        sx={{
          fontSize: 38,
          fontWeight: 700,
          lineHeight: 1.15,
          color: "#171717",
        }}
      >
        {branding.orgName}
      </Typography>

    </Box>

    {/* TAGLINE */}
    <Typography
      sx={{
        mt: 1,
        color: "#666",
        fontSize: 18,
        textAlign: "center",
      }}
    >
      {branding.tagline}
    </Typography>

    {/* DESCRIPTION */}
    <Typography
      sx={{
        mt: 2,
        color: "#777",
        lineHeight: 1.8,
        mb: 4,
        textAlign: "center",
      }}
    >
      {branding.description}
    </Typography>

    {/* DIVIDER */}
    <Box
      sx={{
        height: 1,
        bgcolor: "#E5E7EB",
        my: 3,
      }}
    />

    {/* CAPTCHA */}
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        mb: 2,
      }}
    >
      <ReCAPTCHA
        sitekey={process.env.REACT_APP_SITE_KEY}
        onChange={onCaptchaChange}
      />
    </Box>

    {/* GOOGLE LOGIN */}
    <Button
      fullWidth
      variant="contained"
      onClick={handleSignIn}
      disabled={isSigningIn || !captchaVerified}
      sx={{
        mt: 1,
        height: 56,
        borderRadius: 3,
        background: gradient,
        fontWeight: 700,
        fontSize: 17,
        textTransform: "none",
        boxShadow: "0 12px 30px rgba(0,0,0,.2)",

        "&:hover": {
          opacity: 0.95,
          transform: "translateY(-2px)",
        },

        transition: ".3s",
      }}
    >
      {isSigningIn
        ? "Signing in..."
        : "Continue with Google →"}
    </Button>
  </Box>
</Box>

<style>
{`
@keyframes fadeIn{
  from{
    opacity:0;
    transform:translateY(25px);
  }
  to{
    opacity:1;
    transform:translateY(0);
  }
}
`}
</style>


</Box>
);
};

export default TenantHome;